"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { Icon } from "@iconify/react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

export default function TypescriptCatchBug() {
    const t = useTranslations();

    const [mode, setMode] = useState("ts"); // ts | js
    const [fixed, setFixed] = useState(false);

    const isTs = mode === "ts";
    const prop = fixed ? "firstName" : "firstNam";
    const greeting = t("Ts_catch_greeting");
    const tsError = !isTs ? false : !fixed;

    return (
        <section id="typescript-catch" className="pt-16">
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("Ts_catch_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("Ts_catch_intro")}
            </p>

            <div className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10">
                {/* Glows (teinte TypeScript) */}
                <div className="absolute -top-24 -right-24 w-[460px] h-[460px] bg-blue-500/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] bg-sky-500/15 rounded-full blur-3xl pointer-events-none" />

                {/* Barre de fenêtre (extension selon le mode) */}
                <div className="relative flex items-center gap-2 px-5 py-3 border-b border-white/10">
                    <span className="w-3 h-3 rounded-full bg-red-400/90" />
                    <span className="w-3 h-3 rounded-full bg-yellow-400/90" />
                    <span className="w-3 h-3 rounded-full bg-green-400/90" />
                    <span className="ml-3 inline-flex items-center gap-1.5 text-[11px] font-mono text-gray-400">
                        <Icon
                            icon={isTs ? "logos:typescript-icon" : "logos:javascript"}
                            className="w-3.5 h-3.5"
                            aria-hidden="true"
                        />
                        {isTs ? "script.ts" : "script.js"}
                    </span>
                    <span className="ml-auto inline-flex items-center gap-1.5">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                        </span>
                        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-300">
                            {t("Ts_catch_badge")}
                        </span>
                    </span>
                </div>

                <div className="relative p-5 sm:p-7">
                    {/* Toggle JS / TS */}
                    <div className="flex justify-center mb-7">
                        <div className="inline-flex items-center gap-1 rounded-full bg-black/30 ring-1 ring-white/10 p-1">
                            <button
                                type="button"
                                onClick={() => setMode("js")}
                                className={`rounded-full px-4 py-1.5 text-xs sm:text-sm font-semibold transition-colors ${
                                    !isTs
                                        ? "bg-amber-400 text-bgDark-900"
                                        : "text-gray-400 hover:text-gray-200"
                                }`}
                            >
                                {t("Ts_catch_toggle_js")}
                            </button>
                            <button
                                type="button"
                                onClick={() => setMode("ts")}
                                className={`rounded-full px-4 py-1.5 text-xs sm:text-sm font-semibold transition-colors ${
                                    isTs
                                        ? "bg-blue-500 text-white"
                                        : "text-gray-400 hover:text-gray-200"
                                }`}
                            >
                                {t("Ts_catch_toggle_ts")}
                            </button>
                        </div>
                    </div>

                    <div className="grid lg:grid-cols-2 gap-5 items-start">
                        {/* Éditeur */}
                        <div className="min-w-0 rounded-xl bg-black/30 ring-1 ring-white/10 p-4 font-mono text-[12.5px] sm:text-[13px] leading-relaxed overflow-x-auto">
                            <div>
                                <span className="text-fuchsia-400">const </span>
                                <span className="text-sky-200">user</span>
                                <span className="text-gray-400"> = {"{"}</span>
                            </div>
                            <div className="pl-4">
                                <span className="text-sky-200">firstName</span>
                                <span className="text-gray-400">: </span>
                                <span className="text-emerald-400">&quot;Marie&quot;</span>
                                <span className="text-gray-400">,</span>
                            </div>
                            <div className="pl-4">
                                <span className="text-sky-200">age</span>
                                <span className="text-gray-400">: </span>
                                <span className="text-amber-300">30</span>
                                <span className="text-gray-400">,</span>
                            </div>
                            <div>
                                <span className="text-gray-400">{"};"}</span>
                            </div>
                            <div>&nbsp;</div>
                            <div className="break-words">
                                <span className="text-sky-200">console</span>
                                <span className="text-gray-400">.</span>
                                <span className="text-sky-400">log</span>
                                <span className="text-gray-400">(</span>
                                <span className="text-emerald-400">
                                    &quot;{greeting} &quot;
                                </span>
                                <span className="text-gray-400"> + </span>
                                <span className="text-sky-200">user</span>
                                <span className="text-gray-400">.</span>
                                <span
                                    className={
                                        tsError
                                            ? "text-rose-300 underline decoration-wavy decoration-red-500 decoration-2"
                                            : "text-sky-200"
                                    }
                                >
                                    {prop}
                                </span>
                                <span className="text-gray-400">);</span>
                            </div>
                        </div>

                        {/* Panneau : console (JS) ou diagnostics (TS) */}
                        <div className="min-w-0">
                            {!isTs ? (
                                <div className="rounded-xl bg-black/30 ring-1 ring-white/10 p-4">
                                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-gray-500 mb-3">
                                        <span className="w-1.5 h-1.5 rounded-full bg-gray-500" />
                                        {t("Ts_catch_console_label")}
                                    </div>
                                    <div className="font-mono text-[12.5px] sm:text-[13px] leading-relaxed">
                                        <div className="text-gray-400">
                                            <span className="text-sky-400">$</span>{" "}
                                            node script.js
                                        </div>
                                        <div
                                            className={
                                                fixed
                                                    ? "text-emerald-300"
                                                    : "text-amber-300"
                                            }
                                        >
                                            {greeting}{" "}
                                            {fixed ? "Marie" : "undefined"}
                                        </div>
                                    </div>
                                    {!fixed && (
                                        <div className="mt-3 flex items-start gap-2 text-[13px] text-amber-300/90 leading-snug">
                                            <SolarIcon
                                                name="danger-triangle-bold"
                                                className="w-4 h-4 shrink-0 mt-0.5"
                                            />
                                            {t("Ts_catch_js_note")}
                                        </div>
                                    )}
                                </div>
                            ) : tsError ? (
                                <div className="rounded-xl bg-rose-500/[0.07] ring-1 ring-rose-400/30 p-4">
                                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-rose-300/80 mb-3">
                                        <span className="w-1.5 h-1.5 rounded-full bg-rose-400" />
                                        {t("Ts_catch_problems_label")}
                                    </div>
                                    <div className="flex items-start gap-2">
                                        <SolarIcon
                                            name="close-circle-bold"
                                            className="w-4 h-4 text-rose-400 shrink-0 mt-0.5"
                                        />
                                        <p className="font-mono text-[12.5px] text-rose-200 leading-relaxed mb-0">
                                            Property &apos;firstNam&apos; does not
                                            exist on type. Did you mean
                                            &apos;firstName&apos; ?
                                        </p>
                                    </div>
                                    <p className="mt-3 text-[13px] text-gray-300 leading-snug mb-0">
                                        {t("Ts_catch_ts_note")}
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => setFixed(true)}
                                        className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-blue-500 hover:bg-blue-400 px-3.5 py-2 text-[13px] font-semibold text-white transition active:scale-95"
                                    >
                                        <SolarIcon
                                            name="pen-new-square-bold"
                                            className="w-3.5 h-3.5"
                                        />
                                        {t("Ts_catch_fix_button")}
                                    </button>
                                </div>
                            ) : (
                                <div className="rounded-xl bg-emerald-500/[0.07] ring-1 ring-emerald-400/30 p-4">
                                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-emerald-300/80 mb-3">
                                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                                        {t("Ts_catch_problems_label")}
                                    </div>
                                    <div className="flex items-center gap-2 text-emerald-300 font-semibold text-sm">
                                        <SolarIcon
                                            name="check-circle-bold"
                                            className="w-4 h-4"
                                        />
                                        {t("Ts_catch_ts_ok")}
                                    </div>
                                    <div className="mt-3 font-mono text-[12.5px] text-emerald-300">
                                        {greeting} Marie
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setFixed(false)}
                                        className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-white/[0.06] hover:bg-white/[0.1] ring-1 ring-white/10 px-3 py-1.5 text-xs text-gray-300 transition active:scale-95"
                                    >
                                        <SolarIcon
                                            name="restart-bold-duotone"
                                            className="w-3.5 h-3.5"
                                        />
                                        {t("Ts_catch_reset_button")}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <p className="mt-4 text-sm text-black/60 dark:text-lightBlue/70 max-w-[700px] font-geistSans">
                {t("Ts_catch_caption")}
            </p>
        </section>
    );
}
